/**
 * DialogoProva — crea o corregge una prova (atlante cap. 489): nome, prodotto già scelto e messaggio già scritto. La missione
 * si mostra solo se ce n'è più di una fra quelle che possono partire da un link.
 */

import React, { useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Stack, Switch, TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  motivoDellErrore, salvaProva, type AziendaDelleProve, type DatiProve, type Prova,
} from '../../services/proveService';
import { nomeProdotto } from './nomeProdotto';

export const DialogoProva: React.FC<{
  prova: Prova | null;
  azienda: AziendaDelleProve;
  missioni: DatiProve['missioni'];
  onChiudi: () => void;
  onSalvata: () => void;
}> = ({ prova, azienda, missioni, onChiudi, onSalvata }) => {
  const { t } = useTranslation('prove');
  const [etichetta, setEtichetta] = useState(prova?.etichetta ?? '');
  const [descrizione, setDescrizione] = useState(prova?.descrizione ?? '');
  const [missione, setMissione] = useState(prova?.missione_id ?? missioni[0]?.id ?? '');
  const [categoria, setCategoria] = useState(prova?.categoria ?? '');
  const [messaggio, setMessaggio] = useState(prova?.messaggio ?? '');
  const [attiva, setAttiva] = useState(prova?.attiva ?? true);
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const salva = async () => {
    setInvio(true);
    setErrore(null);
    try {
      await salvaProva({
        ...(prova ? { id: prova.id } : {}),
        etichetta,
        descrizione,
        missione_id: missione,
        categoria: categoria || null,
        messaggio,
        attiva,
      });
      onSalvata();
    } catch (e) {
      const motivo = motivoDellErrore(e);
      setErrore(motivo ? t(`motivi.${motivo}`) : t('messaggi.errore_generico'));
      setInvio(false);
    }
  };

  return (
    <Dialog open onClose={invio ? undefined : onChiudi} fullWidth maxWidth="sm">
      <DialogTitle>{t(prova ? 'dialogo_prova.titolo_modifica' : 'dialogo_prova.titolo_nuova')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {errore && <Alert severity="error">{errore}</Alert>}
          <TextField label={t('dialogo_prova.etichetta')} value={etichetta} onChange={(e) => setEtichetta(e.target.value)} autoFocus fullWidth />
          <TextField label={t('dialogo_prova.descrizione')} value={descrizione} onChange={(e) => setDescrizione(e.target.value)} fullWidth multiline minRows={2} />
          {missioni.length > 1 && (
            <TextField select label={t('dialogo_prova.missione')} value={missione} onChange={(e) => setMissione(e.target.value)} fullWidth>
              {missioni.map((m) => <MenuItem key={m.id} value={m.id}>{m.titolo}</MenuItem>)}
            </TextField>
          )}
          <TextField
            select
            label={t('dialogo_prova.categoria')}
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
            helperText={t('dialogo_prova.categoria_aiuto')}
            fullWidth
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">{t('elenco.lo_chiede_l_assistente')}</MenuItem>
            {azienda.categorie.map((c) => <MenuItem key={c} value={c}>{nomeProdotto(c)}</MenuItem>)}
          </TextField>
          <TextField
            label={t('dialogo_prova.messaggio')}
            value={messaggio}
            onChange={(e) => setMessaggio(e.target.value)}
            helperText={t('dialogo_prova.messaggio_aiuto')}
            fullWidth
            multiline
            minRows={2}
          />
          <FormControlLabel control={<Switch checked={attiva} onChange={(e) => setAttiva(e.target.checked)} />} label={t('dialogo_prova.attiva')} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onChiudi} disabled={invio} sx={{ textTransform: 'none', minHeight: 40 }}>{t('dialogo_prova.annulla')}</Button>
        <Button onClick={() => void salva()} disabled={invio} variant="contained" sx={{ textTransform: 'none', minHeight: 40 }}>{t('dialogo_prova.salva')}</Button>
      </DialogActions>
    </Dialog>
  );
};
